export const NAV_HISTORY_KEY = 'nav_history'
/**
 * @typedef {import('vue-router').RouteLocation} Location
 */

/**
 * Retrieves stored navigation history from session storage
 * @returns {Location|null} Route object or null if not found
 */
function getStoredNavigationHistory () {
  try {
    const stored = sessionStorage.getItem(NAV_HISTORY_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored)
    return parsed && typeof parsed.name === 'string' ? parsed : null
  } catch {
    return null
  }
}

/**
 * Normalizes various route formats to a consistent route object
 * @param {Location['name']|Location|() => Location} routeValue - Route name, object, or getter
 * @param {number} depth - Current recursion depth (internal use)
 * @returns {Location|null} Normalized route object with 'name' property
 */
function normalizeRouteInternal (routeValue, depth = 0) {
  if (!routeValue || depth > 5) return null

  if (typeof routeValue === 'string') {
    const [name, queryString] = routeValue.split('?')
    if (queryString) {
      const query = Object.fromEntries(new URLSearchParams(queryString))
      return { name, query }
    }
    return { name: routeValue }
  }

  if (typeof routeValue === 'function') {
    return normalizeRouteInternal(routeValue(), depth + 1)
  }

  return routeValue
}

/**
 * Normalizes route value to a consistent route object format (no resolution)
 * @param {Location['name']|Location|() => Location} routeValue - Route name, object, or getter
 * @param {Partial<Location>} additionalParams - Additional parameters to merge with the normalized route
 * @returns {Location|null} Normalized route object with 'name' property and additionalParams merged
 */
export function normalizeRoute (routeValue, additionalParams = {}) {
  return _.merge(normalizeRouteInternal(routeValue), additionalParams)
}

/**
 * Vue Router beforeEnter guard to track navigation history
 * Stores the 'from' route in session storage for back navigation
 *
 * @param {Location} to - The target route object
 * @param {Location} from - The source route object
 *
 * @example
 * {
 *   path: 'some-path',
 *   beforeEnter: setBackRouteToLastVisited,
 * }
 */
export function setBackRouteToLastVisited (to, from) {
  const relatedRoutes = ['jobs', 'builds', 'resource']
  const isRelatedNavigation = relatedRoutes.includes(to.name)
    && relatedRoutes.includes(from.name)

  if (from.name && !isRelatedNavigation) {
    sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify({
      name: from.name,
      params: from.params,
      query: from.query,
      hash: from.hash,
    }))
  }
}

/**
 * Gets the back route based on priority:
 * 1. backRouteTo param from state (highest priority)
 * 2. Session storage (last visited route)
 * 3. Route meta backRouteTo (default fallback)
 *
 * @param {Location} route - Current Vue Router route object
 * @returns {Location|null} Route object to navigate back to, or null
 */
export function getBackRoute (route) {
  /**
   * If you pass backRouteTo in a state, it has the highest priority.
   * This is way to preserve vue 2 behavior with the usage of backRouteTo param.
   */
  const { backRouteTo: backRouteToFromState } = window.history.state || {}

  if (backRouteToFromState) {
    const normalized = normalizeRouteInternal(backRouteToFromState)
    if (normalized) return { ...normalized, params: { ...route?.params, ...normalized.params } }
  }

  /**
   * If no backRouteTo param is found in the route, we try to retrieve
   * the last visited route from session storage.
   */
  const storedHistory = getStoredNavigationHistory()
  if (storedHistory && storedHistory.name !== 'login') {
    return storedHistory
  }

  const metaBackRoute = route?.meta?.backRouteTo
  const normalized = normalizeRouteInternal(metaBackRoute)
  if (!normalized) return null

  return { ...normalized, params: { ...route?.params, ...normalized.params } }
}
